import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { toFullName } from '@repo/toolkit/to-full-name'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Button, { ButtonProps } from '@repo/ui/button'
import { EmailIcon, ExclamationMarkIcon } from '@repo/ui/icons'
import Typography from '@repo/ui/typography'

import { Paths } from '../../../../lib/constants/path'
import useAuth from '../../../../lib/hooks/use-auth'
import { RoleEnum } from '../../../../lib/types/enums/users/role.enum'
import { EnrollmentCenter } from '../../../../lib/types/models/enrollment-center/enrollment-center'
import { HostFamilyProperties } from '../../../../lib/types/models/host-family/properties.host-family'
import InformationToolTip from '../../../elements/information-tooltip'
import OlCenterHostFamilyPlacementHistory from './ol-center-host-family-placement-history'

type Props = {
  enrollmentCenter?: EnrollmentCenter
  buttonProps?: ButtonProps
  className?: string
}

const OlCenterEnrollmentTableHostFamily = ({
  enrollmentCenter,
  buttonProps,
  className
}: Props) => {
  const { navigate } = useNavigator()
  const { userRole } = useAuth()

  const hostFamily = enrollmentCenter?.hostFamily

  if ((userRole === RoleEnum.ADMIN && !hostFamily) || !enrollmentCenter) return '-'

  return (
    <div className={cn('flex items-center gap-4', className)}>
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-1">
          <Typography.Link
            variant="label-semibold"
            color={tailwindColors.vividBlue.DEFAULT}
            onClick={(e) => {
              e.stopPropagation()
              if (!hostFamily) return
              if (userRole === RoleEnum.ADMIN)
                navigate(Paths.OL_HOST_FAMILY_INFORMATION.replace(':id', hostFamily.id.toString()))
              else
                navigate(
                  Paths.OL_PERSONAL_SPACE_HOST_FAMILIES_UPDATE_PAGE.replace(
                    ':id',
                    hostFamily.id.toString()
                  )
                )
            }}
          >
            {toFullName(hostFamily?.parent1FirstName, hostFamily?.parent1LastName, {
              options: { mode: 'lastname-firstname' }
            })}
          </Typography.Link>
          {userRole === RoleEnum.Ol && enrollmentCenter.informationSharedToHFByEmail && (
            <InformationToolTip
              contentVariant="secondary"
              align="center"
              icon={
                <div className="flex items-center justify-center bg-grey-light w-fit p-1 rounded-full">
                  <EmailIcon color={tailwindColors.grey.DEFAULT} height="15" width="15" />
                </div>
              }
              size="small"
            >
              <Typography variant={'label-medium'} color={tailwindColors.black.DEFAULT}>
                Information has been emailed to the HF
              </Typography>
            </InformationToolTip>
          )}
        </div>
        {userRole === RoleEnum.Ol && <Button {...buttonProps} type="button" size="small" />}
      </div>
      <div className="flex items-center gap-1">
        {HostFamilyProperties.hasVisibleLecComment(hostFamily, userRole) && (
          <div className="flex items-center justify-center w-6 h-6 bg-red-light rounded-full">
            <div className="flex items-center justify-center h-4 w-4 rounded-full bg-red">
              <ExclamationMarkIcon filled color={tailwindColors.white.DEFAULT} />
            </div>
          </div>
        )}
        {enrollmentCenter?.hostFamilyHistory && enrollmentCenter?.hostFamilyHistory.length > 0 && (
          <OlCenterHostFamilyPlacementHistory
            hostFamilyHistory={enrollmentCenter?.hostFamilyHistory}
          />
        )}
      </div>
    </div>
  )
}

export default OlCenterEnrollmentTableHostFamily
